/**
 * VoiceInspection — floating "Voice inspection" button + recording + parse modal.
 *
 * Flow:
 *   1. Click mic button — starts recording (MediaRecorder API, webm/opus)
 *   2. Click stop — upload to /voice-transcribe, get transcript back
 *   3. Show transcript for review — certifier can edit
 *   4. Click "Parse to checklist" — Claude 4.5 maps transcript to checklist item ids
 *   5. Show proposed answers with confidence + "quoted transcript span"
 *   6. Certifier ticks which ones to apply — never auto-apply
 */
import { useState, useRef, useEffect } from "react";
import {
  Mic, MicOff, Loader2, X, Sparkles, CheckCircle2, HelpCircle,
  Square, Play,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CONFIDENCE_STYLES = {
  high: "bg-emerald-100 text-emerald-900 border-emerald-400",
  medium: "bg-amber-100 text-amber-900 border-amber-400",
  low: "bg-slate-100 text-slate-700 border-slate-300",
};

const VoiceInspection = ({ inspectionId, onApplyAnswers }) => {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [parsed, setParsed] = useState(null);
  const [selected, setSelected] = useState({}); // question_id -> bool
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Voice recording is not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        await transcribeBlob(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordSeconds(0);
      timerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (err) {
      const msg = err?.name === "NotAllowedError" ? "Microphone permission denied — enable it in your browser settings"
        : err?.message || "Could not start recording";
      toast.error(msg);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const transcribeBlob = async (blob) => {
    setTranscribing(true);
    setTranscript("");
    setParsed(null);
    try {
      const form = new FormData();
      const filename = `voice_${Date.now()}.webm`;
      form.append("audio", blob, filename);
      const r = await api.post(`/inspections/${inspectionId}/voice-transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setTranscript(r.data.transcript || "");
      if (!r.data.transcript) {
        toast.warning("Nothing was transcribed — try recording again in a quiet spot");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  const parseToChecklist = async () => {
    if (!transcript.trim()) return;
    setParsing(true);
    setParsed(null);
    try {
      const r = await api.post(`/inspections/${inspectionId}/voice-parse`, { transcript });
      setParsed(r.data);
      const nextSel = {};
      (r.data.matched_answers || []).forEach((m) => { nextSel[m.question_id] = m.confidence !== "low"; });
      setSelected(nextSel);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Parsing failed");
    } finally {
      setParsing(false);
    }
  };

  const applySelected = () => {
    if (!parsed) return;
    const toApply = (parsed.matched_answers || []).filter((m) => selected[m.question_id]);
    if (toApply.length === 0) {
      toast.warning("Tick at least one answer to apply");
      return;
    }
    onApplyAnswers && onApplyAnswers(toApply);
    toast.success(`Applied ${toApply.length} answer(s) — please review each one`);
    setOpen(false);
    setTranscript(""); setParsed(null); setSelected({});
  };

  const reset = () => {
    setTranscript(""); setParsed(null); setSelected({}); setRecordSeconds(0);
  };

  const fmtTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="voice-inspection-open"
        className="h-11 px-4 inline-flex items-center gap-2 border-2 border-purple-600 bg-purple-600 text-white font-bold text-sm hover:bg-purple-700"
        title="Voice-first inspection: talk, AI fills the checklist"
      >
        <Mic size={16} strokeWidth={2.5}/>
        Voice inspection
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto" onClick={() => !recording && setOpen(false)} data-testid="voice-modal">
          <div className="bg-white border-2 border-slate-200 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <header className="px-5 py-4 border-b-2 border-slate-200 flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-700 flex items-center gap-1.5">
                  <Sparkles size={11} strokeWidth={3}/> Whisper + Claude 4.5 · Voice-first inspection
                </p>
                <h2 className="text-lg font-black text-slate-900">Talk your inspection into the checklist</h2>
                <p className="text-xs text-slate-500 mt-0.5">Walk around the pool describing what you see. Whisper transcribes; Claude maps your words to checklist items. You review before anything is saved.</p>
              </div>
              <button onClick={() => !recording && setOpen(false)} disabled={recording} data-testid="voice-modal-close" className="h-9 w-9 inline-flex items-center justify-center hover:bg-slate-100 shrink-0 disabled:opacity-40"><X size={16}/></button>
            </header>

            <div className="p-5 space-y-4">
              {/* Step 1 — record */}
              <section className="p-4 border-2 border-slate-200">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Step 1 · Record</p>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  {!recording && !transcribing && !transcript && (
                    <button onClick={startRecording} data-testid="voice-start" className="h-14 px-5 bg-red-600 text-white font-bold text-sm border-2 border-red-600 hover:bg-red-700 inline-flex items-center gap-2">
                      <Mic size={18} strokeWidth={2.5}/> Start recording
                    </button>
                  )}
                  {recording && (
                    <>
                      <button onClick={stopRecording} data-testid="voice-stop" className="h-14 px-5 bg-slate-900 text-white font-bold text-sm border-2 border-slate-900 hover:bg-slate-800 inline-flex items-center gap-2">
                        <Square size={16} strokeWidth={3}/> Stop &amp; transcribe
                      </button>
                      <span className="inline-flex items-center gap-2 text-red-700 font-bold" data-testid="voice-recording-time">
                        <span className="h-2.5 w-2.5 bg-red-600 rounded-full animate-pulse"/> Recording · {fmtTime(recordSeconds)}
                      </span>
                    </>
                  )}
                  {transcribing && (
                    <span className="inline-flex items-center gap-2 text-slate-700" data-testid="voice-transcribing">
                      <Loader2 size={16} className="animate-spin"/> Transcribing with Whisper…
                    </span>
                  )}
                  {transcript && !recording && !transcribing && (
                    <button onClick={reset} data-testid="voice-reset" className="h-10 px-3 border-2 border-slate-300 bg-white text-slate-900 font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1.5 hover:border-slate-500">
                      <Play size={12} strokeWidth={3}/> Record again
                    </button>
                  )}
                </div>
              </section>

              {/* Step 2 — transcript */}
              {(transcript || transcribing) && (
                <section className="p-4 border-2 border-slate-200">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Step 2 · Review transcript (edit if needed)</p>
                  <textarea
                    data-testid="voice-transcript"
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    disabled={transcribing}
                    rows={5}
                    className="mt-2 w-full p-3 border-2 border-slate-300 focus:border-[#1E1B4B] focus:outline-none text-sm"
                    placeholder="Your transcribed speech will appear here…"
                  />
                  <div className="mt-3 flex flex-wrap gap-2 justify-end">
                    <button
                      onClick={parseToChecklist}
                      disabled={!transcript || parsing}
                      data-testid="voice-parse"
                      className="h-10 px-4 bg-purple-600 text-white font-bold text-xs uppercase tracking-wider hover:bg-purple-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                    >
                      {parsing ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12} strokeWidth={3}/>}
                      Parse to checklist
                    </button>
                  </div>
                </section>
              )}

              {/* Step 3 — parsed answers */}
              {parsed && (
                <section className="p-4 border-2 border-slate-200" data-testid="voice-parsed-section">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Step 3 · Review AI-matched answers (tick the ones to apply)</p>
                  {(parsed.matched_answers || []).length === 0 ? (
                    <p className="mt-3 text-sm text-slate-600">Claude couldn&apos;t map any of your speech to specific checklist items — try being more specific (e.g. &quot;fence height is 1250mm, latch is working&quot;).</p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {parsed.matched_answers.map((m, idx) => (
                        <li key={idx} className={`p-3 border-2 ${CONFIDENCE_STYLES[m.confidence] || CONFIDENCE_STYLES.medium}`} data-testid={`voice-match-${idx}`}>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!selected[m.question_id]}
                              onChange={(e) => setSelected((s) => ({ ...s, [m.question_id]: e.target.checked }))}
                              className="w-5 h-5 mt-0.5 shrink-0"
                              data-testid={`voice-match-check-${idx}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm">{m.question_id}</div>
                              <div className="text-sm mt-0.5">→ <strong>{String(m.proposed_answer)}</strong></div>
                              {m.quoted_transcript_span && (
                                <div className="text-xs italic mt-1 opacity-90">&quot;…{m.quoted_transcript_span}…&quot;</div>
                              )}
                              <div className="text-[10px] mt-1 uppercase tracking-wider opacity-70">{m.confidence} confidence</div>
                            </div>
                            {m.confidence === "high" ? <CheckCircle2 size={14} className="mt-1 shrink-0" strokeWidth={2.5}/> : <HelpCircle size={14} className="mt-1 shrink-0" strokeWidth={2.5}/>}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  {parsed.unrecognised_speech && (
                    <div className="mt-3 p-3 border-2 border-slate-200 bg-slate-50 text-xs text-slate-700">
                      <strong>Not matched:</strong> {parsed.unrecognised_speech}
                    </div>
                  )}
                </section>
              )}
            </div>

            {parsed && (parsed.matched_answers || []).length > 0 && (
              <footer className="px-5 py-4 border-t-2 border-slate-200 bg-slate-50 flex justify-end gap-2">
                <button onClick={() => setOpen(false)} data-testid="voice-cancel" className="h-11 px-4 border-2 border-slate-300 bg-white text-slate-900 font-bold text-sm">Cancel</button>
                <button onClick={applySelected} data-testid="voice-apply" className="h-11 px-5 bg-[#1E1B4B] text-white font-bold text-sm border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] inline-flex items-center gap-2">
                  <Sparkles size={14} strokeWidth={2.5}/> Apply ticked answers
                </button>
              </footer>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Suppress "unused imports" warning for MicOff / Play — kept for future use in disabled states.
export { MicOff, Play };
export default VoiceInspection;
